import type { ApiResponse } from '@/types/api';
import type { AuthUser, LoginPayload, LoginResponse } from '@/types/auth';
import type { AppCapabilities } from '@/types/actions';
import type { RegisterPayload, Business } from '@/types/business';
import type { SubscriptionContext } from '@/types/subscription';
import { apiClient, setStoredToken } from './client';
import { useAuthStore } from '@/stores/authStore';
import { getProfile } from './business.api';
import { useMerchantStore } from '@/stores/merchantStore';

export async function login(payload: LoginPayload): Promise<ApiResponse<LoginResponse>> {
  const { data } = await apiClient.post<ApiResponse<LoginResponse>>('/auth/login', payload);
  return data;
}

export async function logout(): Promise<ApiResponse<null>> {
  const { data } = await apiClient.post<ApiResponse<null>>('/auth/logout');
  return data;
}

export async function getMe(): Promise<ApiResponse<{ user: AuthUser }>> {
  const { data } = await apiClient.get<ApiResponse<{ user: AuthUser }>>('/auth/me');
  return data;
}

export async function getCapabilities(): Promise<ApiResponse<{ capabilities: AppCapabilities; subscription?: SubscriptionContext | null }>> {
  const { data } = await apiClient.get<ApiResponse<{ capabilities: AppCapabilities; subscription?: SubscriptionContext | null }>>(
    '/auth/me/capabilities',
  );
  return data;
}

export async function getBusinesses(): Promise<ApiResponse<{ businesses: Business[] }>> {
  const { data } = await apiClient.get<ApiResponse<{ businesses: Business[] }>>(
    '/auth/me/businesses',
  );
  return data;
}

export async function createBusiness(payload: {
  business_name: string;
  business_type: string;
  business_category_id: number;
  business_phone?: string;
  business_email?: string;
}): Promise<ApiResponse<{ business: Business }>> {
  const { data } = await apiClient.post<ApiResponse<{ business: Business }>>(
    '/auth/me/businesses',
    payload,
  );
  return data;
}

export async function register(
  payload: RegisterPayload,
): Promise<ApiResponse<{ token: string; user: AuthUser; business: Business }>> {
  const { data } = await apiClient.post<
    ApiResponse<{ token: string; user: AuthUser; business: Business }>
  >('/auth/register', payload);
  return data;
}

/**
 * Bootstrap auth state — called after login AND on every app boot (page refresh).
 * 1. Persists the token
 * 2. Fetches /auth/me (user + permissions)
 * 3. Fetches /auth/me/capabilities (nav/page-level boolean map)
 * 4. Fetches /auth/me/businesses (merchant business list)
 * 5. Hydrates the Zustand auth store
 * 6. If merchant — loads business list; auto-selects if only one; loads profile
 */
export async function bootstrapAuth(token: string): Promise<AuthUser> {
  // 1. Persist token so interceptor can pick it up
  setStoredToken(token);

  // 2+3+4. Fetch user, capabilities, and business list in parallel
  const [meRes, capsRes, bizListRes] = await Promise.all([
    getMe(),
    getCapabilities(),
    getBusinesses().catch(() => ({ success: true, message: '', data: { businesses: [] as Business[] } })),
  ]);

  const user = meRes.data.user;
  const capabilities = capsRes.data.capabilities;
  const businesses: Business[] = bizListRes.data.businesses ?? [];

  // 5. Hydrate Zustand auth store
  useAuthStore.getState().setAuth(user, token);
  useAuthStore.getState().setCapabilities(capabilities);
  useAuthStore.getState().setSubscription(capsRes.data.subscription ?? null);

  // 6. If merchant — manage business selection
  const isMerchant =
    user.role === 'merchant' ||
    user.roles?.includes('merchant');

  if (isMerchant && businesses.length > 0) {
    useMerchantStore.getState().setBusinesses(businesses);

    // Restore previously selected business or auto-select if only one
    const savedId = useMerchantStore.getState().selectedBusinessId;
    const savedBiz = businesses.find((b) => b.id === savedId);

    // Stale ID from a previous account — clear it so the new merchant
    // doesn't hit "Business not found or access denied"
    if (savedId && !savedBiz) {
      useMerchantStore.getState().clearBusiness();
    }

    const toSelect = savedBiz ?? (businesses.length === 1 ? businesses[0] : null);

    if (toSelect) {
      useMerchantStore.getState().selectBusiness(toSelect.id);
      try {
        const [bizRes, freshCapsRes] = await Promise.all([
          getProfile(),
          getCapabilities(),
        ]);
        useMerchantStore.getState().setBusiness(bizRes.data.business);
        useAuthStore.getState().setCapabilities(freshCapsRes.data.capabilities);
        useAuthStore.getState().setSubscription(freshCapsRes.data.subscription ?? null);
      } catch {
        // non-fatal — business profile can be loaded later
      }
    }
  }

  return user;
}

